import { useEffect, useRef, useState } from "react";

/**
 * SearchableDropdown
 *
 * Props:
 * - items: string[] | { label: string, value: any }[]
 * - onSelect: (item) => void
 * - itemToString?: (item) => string  // default will read string or .label
 * - buttonPlaceholder?: string // placeholder text shown in button when no selection (default: "Select...")
 * - searchPlaceholder?: string // placeholder text shown in search input (default: "Search...")
 * - className?: string (applied to wrapper)
 * - widthClass?: string (Tailwind width class, e.g. "w-64" or "w-full")
 * - selectedValue?: any // current selected value
 * - searchable?: boolean // whether to show search input (default: true)
 * - onClear?: () => void // callback when clear button is clicked
 */
export default function SearchableDropdown({
  items = [],
  onSelect,
  itemToString,
  buttonPlaceholder = "Select...",
  searchPlaceholder = "Search...",
  className = "",
  widthClass = "w-64",
  selectedValue,
  searchable = true,
  onClear,
}) {
  const normalize = (it) => {
    if (itemToString) return itemToString(it);
    return typeof it === "string" ? it : it?.label ?? String(it);
  };

  const parsedItems = items.map((it) => ({ raw: it, text: normalize(it) }));

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1); // index into filtered
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = searchable
    ? parsedItems.filter((p) =>
        p.text.toLowerCase().includes(query.trim().toLowerCase())
      )
    : parsedItems;

  // open & focus input when opening (only if searchable)
  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    if (!open) {
      setQuery("");
      setHighlight(-1);
    }
  }, [open, searchable]);

  // close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ensure highlight stays within bounds when filtered changes
  useEffect(() => {
    if (filtered.length === 0) setHighlight(-1);
    else if (highlight >= filtered.length) setHighlight(filtered.length - 1);
  }, [filtered.length, highlight]);

  // scroll highlighted item into view
  useEffect(() => {
    if (highlight < 0) return;
    const list = listRef.current;
    const el = list?.children[highlight];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const handleSelect = (entry) => {
    if (!entry) return;
    setOpen(false);
    setQuery("");
    setHighlight(-1);
    onSelect?.(entry.raw);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // choose highlighted or first filtered
      if (filtered.length === 0) return;
      const chosen = highlight >= 0 ? filtered[highlight] : filtered[0];
      handleSelect(chosen);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Get the display label for the selected value
  const selectedLabel = (() => {
    if (!selectedValue) return buttonPlaceholder;
    const match = parsedItems.find(p => {
      const val = typeof p.raw === 'string' ? p.raw : p.raw?.value;
      return val === selectedValue;
    });
    return match ? match.text : buttonPlaceholder;
  })();

  const hasSelection = selectedValue && selectedValue !== '';

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <div ref={wrapperRef} className={`relative ${widthClass} ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex justify-between items-center px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={`truncate text-left ${hasSelection ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
          {selectedLabel}
        </span>
        <div className="flex items-center ml-2">
          {/* Clear button - always show when there's a selection */}
          {hasSelection && (
            <button
              type="button"
              onClick={handleClear}
              className="mr-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title="Clear selection"
            >
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Dropdown arrow */}
          <svg
            className="h-4 w-4 text-gray-500 dark:text-gray-400"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
          >
            <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>

      {/* Menu */}
      {open && (
        <div className="absolute left-0 mt-2 z-50 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg w-full">
          {/* Search input - only show if searchable */}
          {searchable && (
            <div className="p-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlight(0);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* List */}
          <ul
            ref={listRef}
            role="listbox"
            aria-activedescendant={highlight >= 0 ? `dd-item-${highlight}` : undefined}
            className="max-h-48 overflow-auto py-1"
            style={{ minWidth: "12rem" }}
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">No results</li>
            ) : (
              filtered.map((entry, idx) => {
                const isHighlighted = idx === highlight;
                return (
                  <li
                    id={`dd-item-${idx}`}
                    key={idx}
                    role="option"
                    aria-selected={isHighlighted}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => {
                      // mouseDown prevents blur before click
                      e.preventDefault();
                    }}
                    onClick={() => handleSelect(entry)}
                    className={`px-4 py-2 text-sm cursor-pointer ${
                      isHighlighted
                        ? "bg-blue-100 text-blue-900 dark:bg-blue-600 dark:text-white"
                        : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    }`}
                  >
                    {entry.text}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
